import { inject, Injectable } from '@angular/core';
import {
    IChecklistRemindTimeData,
    IChecklistTimeInterval,
    IChecklistTimeIntervalData,
} from '@fi-shared/checklist/checklist-v2';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { FiDateTimeFormatOptions, isDefined } from '@fi-shared/utils';
import { FiChecklistTimeIntervalDataType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-data-type';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import moment = require('moment-timezone');

export interface IChecklistTimeIntervalViewData {
    timeInterval: IChecklistTimeInterval;
    startDateTime: Date;
    endDateTime: Date;
    durationMS: number;
}

@Injectable()
export class FiChecklist2ScheduleTimeIntervalHelperService {
    private fiFormatDateService = inject(FiFormatDateService);

    getShortestTimeInterval(
        timeIntervals: IChecklistTimeInterval[],
        timeIntervalType: FiChecklistTimeIntervalType,
    ): IChecklistTimeIntervalViewData {
        const viewData = this.getTimeIntervalViewData(timeIntervals, timeIntervalType);
        viewData.sort(comparator((item) => item.durationMS));
        return viewData[0];
    }

    private getTimeIntervalViewData(
        timeIntervals: IChecklistTimeInterval[],
        timeIntervalType: FiChecklistTimeIntervalType,
    ): IChecklistTimeIntervalViewData[] {
        const viewData: IChecklistTimeIntervalViewData[] = [];
        timeIntervals
            .filter((timeInterval) => timeInterval.IsEnabled)
            .forEach((timeInterval) => {
                try {
                    const dates = this.getTimeIntervalDates(timeInterval, timeIntervalType);
                    const durationMS = dates.endDateTime.getTime() - dates.startDateTime.getTime();
                    const viewItem: IChecklistTimeIntervalViewData = {
                        timeInterval: timeInterval,
                        startDateTime: dates.startDateTime,
                        endDateTime: dates.endDateTime,
                        durationMS: durationMS,
                    };
                    viewData.push(viewItem);
                } catch {}
            });
        return viewData;
    }

    private getTimeIntervalDates(
        timeInterval: IChecklistTimeInterval,
        timeIntervalType: FiChecklistTimeIntervalType,
    ): {
        startDateTime: Date;
        endDateTime: Date;
    } {
        const now = new Date();
        const isOnDemand: boolean = timeIntervalType === FiChecklistTimeIntervalType.OnDemand;

        const startDatePart = this.getDatePartFromTimeInterval(
            timeInterval.Start,
            timeIntervalType,
            new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes()),
            true,
        );
        let startDateTime = isOnDemand
            ? startDatePart
            : this.getDateTimeFromTimeInterval(startDatePart, timeInterval.Start);
        const endDatePart = this.getDatePartFromTimeInterval(timeInterval.End, timeIntervalType, startDatePart);
        let endDateTime = isOnDemand ? endDatePart : this.getDateTimeFromTimeInterval(endDatePart, timeInterval.End);

        if (timeIntervalType === FiChecklistTimeIntervalType.RecurringMonthly) {
            // https://chainformation.atlassian.net/browse/FI-5279
            if (endDateTime <= startDateTime) {
                if (timeInterval.Start.DataType === FiChecklistTimeIntervalDataType.FirstWeekDay) {
                    // When Start date is First [day] of month and Start date comes after End date, we automatically change Start date to first day of the month for this year.
                    // When start date is conflicting, we set it to first day of month and also change start time to 00:00.
                    /*console.log(
                        'getTimeIntervalDates CONFLICT',
                        this.fiFormatDateService.formatDate(startDateTime, FiDateTimeFormatOptions, {
                            showToday: false,
                        }),
                        this.fiFormatDateService.formatDate(endDateTime, FiDateTimeFormatOptions, { showToday: false }),
                    );*/

                    startDateTime = new Date(startDateTime.getTime());
                    startDateTime.setDate(1);
                    if (endDateTime <= startDateTime) {
                        startDateTime.setHours(0, 0, 0);
                    }
                } else if (timeInterval.End.DataType == FiChecklistTimeIntervalDataType.LastWeekDay) {
                    // When End date is Last [day] of month and End date comes earlier than Start date, we automatically change End date to last day of the month for this year.
                    // When end date is conflicting, we set it to last day of month and also change end time to 23:59:59.

                    /*console.log(
                        'getTimeIntervalDates CONFLICT',
                        this.fiFormatDateService.formatDate(startDateTime, FiDateTimeFormatOptions, {
                            showToday: false,
                        }),
                        this.fiFormatDateService.formatDate(endDateTime, FiDateTimeFormatOptions, { showToday: false }),
                    );*/

                    const daysInMonth = this.getDaysInMonth(endDateTime);
                    endDateTime = new Date(endDateTime.getTime());
                    endDateTime.setDate(daysInMonth);
                    if (endDateTime <= startDateTime) {
                        endDateTime.setHours(23, 59, 59);
                    }
                }
            }
        }

        return {
            startDateTime,
            endDateTime,
        };
    }

    private getDatePartFromTimeInterval(
        data: IChecklistTimeIntervalData,
        timeIntervalType: FiChecklistTimeIntervalType,
        currentDatePart: Date,
        isStartInterval?: boolean,
    ): Date {
        if ([FiChecklistTimeIntervalType.OneTime, FiChecklistTimeIntervalType.Audit].includes(timeIntervalType)) {
            if (isDefined(data.Year) && isDefined(data.Month) && isDefined(data.Day)) {
                const datePart = new Date(data.Year, data.Month - 1, data.Day); // timeIntervalData.Month is from 1 to 12
                return datePart;
            }
        }

        if (FiChecklistTimeIntervalType.OnDemand === timeIntervalType) {
            return isStartInterval ? currentDatePart : this.getOnDemandEndDate(currentDatePart, data);
        }

        if (
            [FiChecklistTimeIntervalType.RecurringDaily, FiChecklistTimeIntervalType.RecurringWeekly].includes(
                timeIntervalType,
            )
        ) {
            if (isDefined(data.DayOfWeek)) {
                const currentDayOfWeek = this.getNormalizedDayOfWeek(currentDatePart.getDay());
                let dayOfWeekDiff = (data.IsNextDay ? data.DayOfWeek + 1 : data.DayOfWeek) - currentDayOfWeek;
                if (dayOfWeekDiff < 0) {
                    dayOfWeekDiff += 7;
                }
                const datePart = new Date(
                    currentDatePart.getFullYear(),
                    currentDatePart.getMonth(),
                    currentDatePart.getDate() + dayOfWeekDiff,
                );
                return datePart;
            }
        }

        if (timeIntervalType === FiChecklistTimeIntervalType.RecurringMonthly) {
            if (isDefined(data.DataType) && isDefined(data.Month)) {
                if (data.DataType === FiChecklistTimeIntervalDataType.Day && isDefined(data.Day)) {
                    const datePart = new Date(currentDatePart.getFullYear(), data.Month - 1, data.Day);
                    return datePart;
                }
                if (data.DataType === FiChecklistTimeIntervalDataType.FirstWeekDay && isDefined(data.DayOfWeek)) {
                    const datePart = new Date(currentDatePart.getFullYear(), data.Month - 1, 1);
                    while (this.getNormalizedDayOfWeek(datePart.getDay()) !== data.DayOfWeek) {
                        datePart.setDate(datePart.getDate() + 1);
                    }
                    return datePart;
                }
                if (data.DataType === FiChecklistTimeIntervalDataType.LastWeekDay && isDefined(data.DayOfWeek)) {
                    const daysInMonth = this.getDaysInMonth(currentDatePart);
                    const datePart = new Date(currentDatePart.getFullYear(), data.Month - 1, daysInMonth);
                    while (this.getNormalizedDayOfWeek(datePart.getDay()) !== data.DayOfWeek) {
                        datePart.setDate(datePart.getDate() - 1);
                    }
                    return datePart;
                }
                if (data.DataType === FiChecklistTimeIntervalDataType.LastDay) {
                    const daysInMonth = this.getDaysInMonth(currentDatePart);
                    const datePart = new Date(currentDatePart.getFullYear(), data.Month - 1, daysInMonth);
                    return datePart;
                }
            }
        }
        if (timeIntervalType === FiChecklistTimeIntervalType.RecurringYearly) {
            if (isDefined(data.Month) && isDefined(data.Day)) {
                const datePart = new Date(currentDatePart.getFullYear(), data.Month - 1, data.Day); // timeIntervalData.Month is from 1 to 12
                return datePart;
            }
        }

        throw new Error('GetDatePartFromTimeInterval');
    }

    private getDaysInMonth(date: Date): number {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    }

    getOnDemandEndDate(startDate: Date, endIntervalData: IChecklistTimeIntervalData): Date {
        if (endIntervalData.hasOwnProperty('Year')) return moment(startDate).add(endIntervalData.Year, 'y').toDate();

        if (endIntervalData.hasOwnProperty('Month')) return moment(startDate).add(endIntervalData.Month, 'M').toDate();

        if (endIntervalData.hasOwnProperty('Week')) return moment(startDate).add(endIntervalData.Week, 'w').toDate();

        if (endIntervalData.hasOwnProperty('Day')) return moment(startDate).add(endIntervalData.Day, 'd').toDate();

        if (endIntervalData.hasOwnProperty('Hour')) return moment(startDate).add(endIntervalData.Hour, 'h').toDate();

        if (endIntervalData.hasOwnProperty('Minute'))
            return moment(startDate).add(endIntervalData.Minute, 'm').toDate();
    }

    private getDateTimeFromTimeInterval(datePart: Date, data: IChecklistTimeIntervalData): Date {
        if (isDefined(data.Hour) && isDefined(data.Minute)) {
            return new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), data.Hour, data.Minute);
        }
        throw new Error('TimeIntervalData time part is null');
    }

    private getNormalizedDayOfWeek(dayOfWeek: number): number {
        return dayOfWeek !== 0 ? dayOfWeek : 7;
    }

    isRemindTimeDataFilledAndInvalid(
        shortestTimeInterval: IChecklistTimeIntervalViewData,
        remindTimeData: IChecklistRemindTimeData,
    ): boolean {
        return (
            isDefined(shortestTimeInterval) &&
            isDefined(remindTimeData.Month) &&
            isDefined(remindTimeData.Day) &&
            isDefined(remindTimeData.Hour) &&
            isDefined(remindTimeData.Minute) &&
            !this.isRemindTimeDataValid(shortestTimeInterval, remindTimeData)
        );
    }

    private isRemindTimeDataValid(
        shortestTimeInterval: IChecklistTimeIntervalViewData,
        remindTimeData: IChecklistRemindTimeData,
    ): boolean {
        return (
            this.getRemindDateTimeForShortestPeriod(shortestTimeInterval, remindTimeData) >=
            shortestTimeInterval.startDateTime
        );
    }

    private getRemindDateTimeForShortestPeriod(
        shortestTimeInterval: IChecklistTimeIntervalViewData,
        remindTimeData: IChecklistRemindTimeData,
    ): Date {
        const remindDateTime: Date = new Date(shortestTimeInterval.endDateTime.getTime());
        remindDateTime.setMonth(remindDateTime.getMonth() - remindTimeData.Month);
        remindDateTime.setDate(remindDateTime.getDate() - remindTimeData.Day);
        remindDateTime.setHours(remindDateTime.getHours() - remindTimeData.Hour);
        remindDateTime.setMinutes(remindDateTime.getMinutes() - remindTimeData.Minute);
        return remindDateTime;
    }

    isRemindTimeSet(remindTime: IChecklistRemindTimeData): boolean {
        return Object.keys(remindTime).some((key: string) => remindTime[key] > 0);
    }
}
